import React, { useCallback, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'
import { Breakpoint, getBreakpoint } from '~theme'

interface Props {
  starting?: Breakpoint
  ending?: Breakpoint
}

const BreakpointGuard: React.FC<Props> = ({ starting, ending, children }) => {
  const { breakpoints } = useTheme()

  const checkBreakpoint = useCallback(() => {
    if (starting !== undefined && window.innerWidth < getBreakpoint(starting, breakpoints)) {
      return false
    }
    if (ending !== undefined && window.innerWidth >= getBreakpoint(ending, breakpoints)) {
      return false
    }
    return true
  }, [starting, ending, breakpoints])

  const [isRendering, setIsRendering] = useState(checkBreakpoint())

  const onResize = useCallback(() => {
    const isNewRendering = checkBreakpoint()

    if (isRendering !== isNewRendering) {
      setIsRendering(isNewRendering)
    }
  }, [isRendering, checkBreakpoint])

  useEffect(() => {
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [onResize])

  useEffect(() => {
    onResize()
  }, [starting, ending, breakpoints, onResize])

  if (!isRendering) {
    return null
  }

  /* as usual, returning an array conflicts with TS definition of React.FC, but eslint complains */
  /* eslint-disable react/jsx-no-useless-fragment */
  return <>{children}</>
}

export default BreakpointGuard
